// Substitution Management Absence CRUD API

/// <reference lib="dom" />

import { handleApiCall, ValidationError, ConflictError, NotFoundError } from './errors'
import { getAccessToken } from './auth-token'
import type {
  AbsenceRecord,
  AbsenceWithStatus,
  AbsenceDetail,
  AbsenceDayEntry,
  AbsenceDayStatus,
  CreateAbsenceRequest,
  GetAbsencesParams,
  AbsenceStatus,
} from '@timetable/types'

const SUPABASE_URL = (import.meta as any).env?.VITE_SUPABASE_URL as string
const SUPABASE_ANON_KEY = (import.meta as any).env?.VITE_SUPABASE_ANON_KEY as string

if (!SUPABASE_URL || !SUPABASE_ANON_KEY) {
  throw new Error('Missing Supabase environment variables')
}

/**
 * Helper: Derive absence status from assignments array
 */
function deriveAbsenceStatusFromAssignments(assignments: any[]): AbsenceStatus {
  if (!assignments || assignments.length === 0) {
    return 'pending'
  }

  const hasDraft = assignments.some((a) => a.status === 'draft')
  const hasPosted = assignments.some((a) => a.status === 'posted')
  const allPosted = assignments.every((a) => a.status === 'posted')

  if (hasDraft) {
    return 'draft_pending'
  }
  if (allPosted) {
    return 'posted'
  }
  if (hasPosted) {
    return 'active'
  }
  return 'pending'
}

/**
 * Create a new absence
 * Validations:
 * - end_date >= start_date
 * - temporary_duty: enforce start_date === end_date
 * - temporary_duty: require start_time and end_time; validate start_time < end_time
 * - Check overlapping absences
 */
export async function createAbsence(req: CreateAbsenceRequest): Promise<AbsenceRecord> {
  return handleApiCall(async () => {
    const accessToken = getAccessToken()
    if (!accessToken) throw new Error('Not authenticated')

    // Validation 1: end_date >= start_date
    if (new Date(req.end_date) < new Date(req.start_date)) {
      throw new ValidationError('End date must be on or after start date', {
        end_date: 'End date must be on or after start date',
      })
    }

    // Validation 2: temporary_duty must be single-day
    if (req.absence_type === 'temporary_duty' && req.start_date !== req.end_date) {
      throw new ValidationError('Temporary Duty absences must be single-day only', {
        absence_type: 'Temporary Duty absences must be single-day only',
      })
    }

    // Validation 3: temporary_duty requires start_time and end_time
    if (req.absence_type === 'temporary_duty') {
      if (!req.start_time || !req.end_time) {
        throw new ValidationError('Temporary Duty absences require start and end times', {
          start_time: 'Start time is required for Temporary Duty',
          end_time: 'End time is required for Temporary Duty',
        })
      }
      if (req.start_time >= req.end_time) {
        throw new ValidationError('Start time must be before end time', {
          start_time: 'Start time must be before end time',
        })
      }
    }

    // Validation 4: Check for overlapping absences
    const overlapParams = new URLSearchParams()
    overlapParams.append('teacher_id', `eq.${req.teacher_id}`)
    overlapParams.append('status', `neq.cancelled`)
    overlapParams.append('start_date', `lte.${req.end_date}`)
    overlapParams.append('end_date', `gte.${req.start_date}`)
    overlapParams.append('select', 'id,start_date,end_date')

    const overlapResponse = await fetch(`${SUPABASE_URL}/rest/v1/absences?${overlapParams}`, {
      headers: {
        'apikey': SUPABASE_ANON_KEY,
        'Authorization': `Bearer ${accessToken}`,
      },
    })

    if (overlapResponse.ok) {
      const overlapping = await overlapResponse.json()
      if (overlapping.length > 0) {
        throw new ConflictError('This teacher already has an absence during this period', overlapping)
      }
    }

    // Insert absence with status='pending' and date=start_date (legacy)
    const insertData = {
      school_id: req.school_id,
      teacher_id: req.teacher_id,
      date: req.start_date, // legacy column
      start_date: req.start_date,
      end_date: req.end_date,
      absence_type: req.absence_type,
      start_time: req.start_time || null,
      end_time: req.end_time || null,
      reason: req.reason || null,
      status: 'pending',
      created_by: req.created_by || null,
    }

    const response = await fetch(`${SUPABASE_URL}/rest/v1/absences`, {
      method: 'POST',
      headers: {
        'apikey': SUPABASE_ANON_KEY,
        'Authorization': `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
        'Prefer': 'return=representation',
      },
      body: JSON.stringify(insertData),
    })

    if (!response.ok) {
      const error = await response.json()
      throw new Error(error.message || 'Failed to create absence')
    }

    const data: any[] = await response.json()
    const absence = data[0]

    // ── Conflict invalidation ──────────────────────────────────────────────
    // Draft assignments: null out the substitute so the admin is forced to
    // reassign before posting.
    // Posted assignments: we cannot silently change them (notifications already
    // sent), but we flag them via unassigned_reason so the UI can warn the admin
    // to recall and re-draft.
    try {
      const conflictParams = new URLSearchParams()
      conflictParams.append('select', 'id,status')
      conflictParams.append('substitute_teacher_id', `eq.${req.teacher_id}`)
      conflictParams.append('status', `in.(draft,posted)`)
      conflictParams.append('date', `gte.${req.start_date}`)
      conflictParams.append('date', `lte.${req.end_date}`)

      const conflictResponse = await fetch(
        `${SUPABASE_URL}/rest/v1/assignments?${conflictParams}`,
        {
          headers: {
            apikey: SUPABASE_ANON_KEY,
            Authorization: `Bearer ${accessToken}`,
          },
        }
      )

      if (conflictResponse.ok) {
        const conflicting: { id: string; status: string }[] = await conflictResponse.json()

        const draftIds = conflicting.filter((a) => a.status === 'draft').map((a) => a.id)
        const postedIds = conflicting.filter((a) => a.status === 'posted').map((a) => a.id)

        // Drafts: clear the substitute so the period shows as unassigned
        if (draftIds.length > 0) {
          await fetch(`${SUPABASE_URL}/rest/v1/assignments?id=in.(${draftIds.join(',')})`, {
            method: 'PATCH',
            headers: {
              apikey: SUPABASE_ANON_KEY,
              Authorization: `Bearer ${accessToken}`,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              substitute_teacher_id: null,
              unassigned_reason: 'substitute_now_absent',
              assignment_method: 'automatic',
            }),
          })
        }

        // Posted: cannot change the substitute (notification already sent),
        // but stamp the reason so the UI can surface a "recall needed" warning.
        if (postedIds.length > 0) {
          await fetch(`${SUPABASE_URL}/rest/v1/assignments?id=in.(${postedIds.join(',')})`, {
            method: 'PATCH',
            headers: {
              apikey: SUPABASE_ANON_KEY,
              Authorization: `Bearer ${accessToken}`,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              unassigned_reason: 'substitute_now_absent',
            }),
          })
        }
      }
    } catch (err) {
      // Non-fatal — log and continue.
      console.warn('Failed to invalidate conflicting assignments:', err)
    }

    // TODO: writeAuditEntry('absence_created', absence.id, ...)

    return absence as AbsenceRecord
  })
}

/**
 * Get absences with filters and pagination
 */
export async function getAbsences(params: GetAbsencesParams): Promise<AbsenceWithStatus[]> {
  return handleApiCall(async () => {
    const accessToken = getAccessToken()
    if (!accessToken) throw new Error('Not authenticated')

    const queryParams = new URLSearchParams()
    
    // Select with teacher join and embedded assignments
    queryParams.append(
      'select',
      '*,teacher:teachers(*),assignments(id,date,status,substitute_teacher_id,unassigned_reason)'
    )

    // School filter
    queryParams.append('school_id', `eq.${params.school_id}`)

    // Status filter
    // 'active' and 'draft_pending' are derived from assignments client-side,
    // so we can't filter them directly in the DB query. Fetch all non-cancelled
    // absences and post-filter below.
    const isDerivedStatus = params.status === 'active' || params.status === 'draft_pending'
    if (params.status && params.status !== 'all' && !isDerivedStatus) {
      queryParams.append('status', `eq.${params.status}`)
    } else if (isDerivedStatus) {
      // Exclude cancelled so we don't pull unnecessary rows
      queryParams.append('status', `neq.cancelled`)
    }

    // Search by teacher name (requires ilike on joined table - simplified here)
    // Note: For proper teacher name search, we'd need to use a more complex query or post-filter
    // For now, we'll fetch and filter client-side if search is provided

    // Sort mapping
    const sortMap: Record<string, string> = {
      start_date_desc: 'start_date.desc',
      start_date_asc: 'start_date.asc',
      teacher_name: 'teacher_id.asc', // Simplified - would need join sort
      status: 'status.asc',
    }
    const sortBy = params.sort_by || 'start_date_desc'
    queryParams.append('order', sortMap[sortBy] || 'start_date.desc')

    // Pagination
    const page = params.page || 1
    const pageSize = params.page_size || 20
    const offset = (page - 1) * pageSize
    queryParams.append('limit', pageSize.toString())
    queryParams.append('offset', offset.toString())

    const response = await fetch(`${SUPABASE_URL}/rest/v1/absences?${queryParams}`, {
      headers: {
        'apikey': SUPABASE_ANON_KEY,
        'Authorization': `Bearer ${accessToken}`,
      },
    })

    if (!response.ok) {
      const error = await response.json()
      throw new Error(error.message || 'Failed to fetch absences')
    }

    const absences: any[] = await response.json()

    // Compute AbsenceWithStatus fields
    const result: AbsenceWithStatus[] = absences.map((absence) => {
      const assignments = absence.assignments || []
      
      // Simple date diff for working_days_count (placeholder)
      const startDate = new Date(absence.start_date)
      const endDate = new Date(absence.end_date)
      const daysDiff = Math.floor((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24)) + 1
      const working_days_count = daysDiff

      // Posted days: distinct dates with status='posted'
      const postedDates = new Set(
        assignments.filter((a: any) => a.status === 'posted').map((a: any) => a.date)
      )
      const posted_days_count = postedDates.size

      // Draft days: distinct dates with status='draft' (no posted on same date)
      const draftDates = new Set(
        assignments
          .filter((a: any) => a.status === 'draft')
          .map((a: any) => a.date)
          .filter((date: string) => !postedDates.has(date))
      )
      const draft_days_count = draftDates.size

      // Unassigned periods: assignments where substitute_teacher_id is null
      const unassigned_periods_count = assignments.filter(
        (a: any) => a.substitute_teacher_id === null
      ).length

      // Substitute conflicts: posted assignments where the substitute is now absent
      const substitute_conflict_count = assignments.filter(
        (a: any) => a.status === 'posted' && a.unassigned_reason === 'substitute_now_absent'
      ).length

      // Notification failures: 0 (TODO)
      const notification_failures_count = 0
      // Next unposted date: earliest date with no posted assignment
      let next_unposted_date: string | null = null
      const allDates: string[] = []
      for (let d = new Date(absence.start_date); d <= endDate; d.setDate(d.getDate() + 1)) {
        const dateStr = d.toISOString().split('T')[0]
        allDates.push(dateStr)
      }
      for (const date of allDates) {
        if (!postedDates.has(date)) {
          next_unposted_date = date
          break
        }
      }

      return {
        ...absence,
        working_days_count,
        posted_days_count,
        draft_days_count,
        unassigned_periods_count,
        substitute_conflict_count,
        notification_failures_count,
        next_unposted_date,
      } as AbsenceWithStatus
    })

    // Post-filter for derived statuses (active / draft_pending)
    // assignments is fetched via the select join but not in the TypeScript type,
    // so we cast to any to access it at runtime.
    let filtered = result
    if (params.status === 'active' || params.status === 'draft_pending') {
      filtered = result.filter((a) => {
        const derived = deriveAbsenceStatusFromAssignments((a as any).assignments || [])
        return derived === params.status
      })
    }

    // Client-side search filter if provided
    if (params.search) {
      const searchLower = params.search.toLowerCase()
      return filtered.filter((a) => a.teacher?.name?.toLowerCase().includes(searchLower))
    }

    return filtered
  })
}

/**
 * Get absence by ID with full details
 */
export async function getAbsenceById(id: string): Promise<AbsenceDetail> {
  return handleApiCall(async () => {
    const accessToken = getAccessToken()
    if (!accessToken) throw new Error('Not authenticated')

    const queryParams = new URLSearchParams()
    queryParams.append(
      'select',
      '*,teacher:teachers(*),assignments(id,date,status,substitute_teacher_id,class_section_id,period_id,subject_id)'
    )
    queryParams.append('id', `eq.${id}`)

    const response = await fetch(`${SUPABASE_URL}/rest/v1/absences?${queryParams}`, {
      headers: {
        'apikey': SUPABASE_ANON_KEY,
        'Authorization': `Bearer ${accessToken}`,
        'Accept': 'application/vnd.pgrst.object+json',
      },
    })

    if (!response.ok) {
      if (response.status === 404 || response.status === 406) {
        throw new NotFoundError('Absence not found')
      }
      const error = await response.json()
      throw new Error(error.message || 'Failed to fetch absence')
    }

    const absence: any = await response.json()
    const assignments = absence.assignments || []

    // Compute days array
    const days: AbsenceDayEntry[] = []
    const startDate = new Date(absence.start_date)
    const endDate = new Date(absence.end_date)

    for (let d = new Date(startDate); d <= endDate; d.setDate(d.getDate() + 1)) {
      const dateStr = d.toISOString().split('T')[0]
      const dayOfWeek = d.getDay()
      const is_working_day = dayOfWeek !== 0 && dayOfWeek !== 6 // Approximate

      const dayAssignments = assignments.filter((a: any) => a.date === dateStr)
      const assigned_count = dayAssignments.filter((a: any) => a.substitute_teacher_id !== null).length
      const unassigned_count = dayAssignments.filter((a: any) => a.substitute_teacher_id === null).length

      // Derive status
      let status: AbsenceDayStatus = 'pending'
      if (absence.status === 'cancelled') {
        status = 'cancelled'
      } else if (!is_working_day) {
        status = 'non_working_day'
      } else if (dayAssignments.length === 0) {
        status = 'pending'
      } else {
        const hasDraft = dayAssignments.some((a: any) => a.status === 'draft')
        const hasPosted = dayAssignments.some((a: any) => a.status === 'posted')
        if (hasPosted && !hasDraft) {
          status = 'posted'
        } else if (hasDraft) {
          status = 'draft_pending'
        } else {
          status = 'pending'
        }
      }

      days.push({
        date: dateStr,
        status,
        is_working_day,
        assigned_count,
        unassigned_count,
      })
    }

    // Compute summary fields (same as getAbsences)
    const working_days_count = days.filter((d) => d.is_working_day).length
    const postedDates = new Set(
      assignments.filter((a: any) => a.status === 'posted').map((a: any) => a.date)
    )
    const posted_days_count = postedDates.size
    const draftDates = new Set(
      assignments
        .filter((a: any) => a.status === 'draft')
        .map((a: any) => a.date)
        .filter((date: string) => !postedDates.has(date))
    )
    const draft_days_count = draftDates.size
    const unassigned_periods_count = assignments.filter(
      (a: any) => a.substitute_teacher_id === null
    ).length
    const notification_failures_count = 0
    let next_unposted_date: string | null = null
    for (const day of days) {
      if (day.is_working_day && !postedDates.has(day.date)) {
        next_unposted_date = day.date
        break
      }
    }

    return {
      ...absence,
      working_days_count,
      posted_days_count,
      draft_days_count,
      unassigned_periods_count,
      notification_failures_count,
      next_unposted_date,
      days,
    } as AbsenceDetail
  })
}

/**
 * Update absence end date
 * - Extend: just update end_date
 * - Shorten: check for posted assignments beyond newEndDate
 */
export async function updateAbsenceEndDate(
  id: string,
  newEndDate: string,
  force?: boolean
): Promise<AbsenceRecord> {
  return handleApiCall(async () => {
    const accessToken = getAccessToken()
    if (!accessToken) throw new Error('Not authenticated')

    // Fetch current absence
    const absence = await getAbsenceById(id)
    const currentEndDate = new Date(absence.end_date)
    const targetEndDate = new Date(newEndDate)

    if (targetEndDate >= currentEndDate) {
      // Extending: just update end_date
      const response = await fetch(`${SUPABASE_URL}/rest/v1/absences?id=eq.${id}`, {
        method: 'PATCH',
        headers: {
          'apikey': SUPABASE_ANON_KEY,
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
          'Prefer': 'return=representation',
        },
        body: JSON.stringify({ end_date: newEndDate }),
      })

      if (!response.ok) {
        const error = await response.json()
        throw new Error(error.message || 'Failed to update absence')
      }

      const data: any[] = await response.json()
      // TODO: writeAuditEntry('absence_edited', id, ...)
      return data[0] as AbsenceRecord
    } else {
      // Shortening: check for posted assignments beyond newEndDate
      const assignmentsParams = new URLSearchParams()
      assignmentsParams.append('absence_id', `eq.${id}`)
      assignmentsParams.append('date', `gt.${newEndDate}`)
      assignmentsParams.append('status', `eq.posted`)
      assignmentsParams.append('select', 'id,date')

      const assignmentsResponse = await fetch(
        `${SUPABASE_URL}/rest/v1/assignments?${assignmentsParams}`,
        {
          headers: {
            'apikey': SUPABASE_ANON_KEY,
            'Authorization': `Bearer ${accessToken}`,
          },
        }
      )

      if (assignmentsResponse.ok) {
        const postedBeyond = await assignmentsResponse.json()
        if (postedBeyond.length > 0 && !force) {
          throw new ConflictError(
            'Cannot shorten absence: posted assignments exist beyond the new end date',
            postedBeyond
          )
        }

        if (postedBeyond.length > 0 && force) {
          // Mark posted assignments as cancelled
          await fetch(`${SUPABASE_URL}/rest/v1/assignments?absence_id=eq.${id}&date=gt.${newEndDate}&status=eq.posted`, {
            method: 'PATCH',
            headers: {
              'apikey': SUPABASE_ANON_KEY,
              'Authorization': `Bearer ${accessToken}`,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ status: 'cancelled' }),
          })
          // TODO: trigger cancellation notifications
        }
      }

      // Delete draft assignments beyond newEndDate
      await fetch(`${SUPABASE_URL}/rest/v1/assignments?absence_id=eq.${id}&date=gt.${newEndDate}&status=eq.draft`, {
        method: 'DELETE',
        headers: {
          'apikey': SUPABASE_ANON_KEY,
          'Authorization': `Bearer ${accessToken}`,
        },
      })

      // Update end_date
      const response = await fetch(`${SUPABASE_URL}/rest/v1/absences?id=eq.${id}`, {
        method: 'PATCH',
        headers: {
          'apikey': SUPABASE_ANON_KEY,
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
          'Prefer': 'return=representation',
        },
        body: JSON.stringify({ end_date: newEndDate }),
      })

      if (!response.ok) {
        const error = await response.json()
        throw new Error(error.message || 'Failed to update absence')
      }

      const data: any[] = await response.json()
      // TODO: writeAuditEntry('absence_edited', id, ...)
      return data[0] as AbsenceRecord
    }
  })
}

/**
 * Update absence reason
 */
export async function updateAbsenceReason(id: string, reason: string): Promise<AbsenceRecord> {
  return handleApiCall(async () => {
    const accessToken = getAccessToken()
    if (!accessToken) throw new Error('Not authenticated')

    const response = await fetch(`${SUPABASE_URL}/rest/v1/absences?id=eq.${id}`, {
      method: 'PATCH',
      headers: {
        'apikey': SUPABASE_ANON_KEY,
        'Authorization': `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
        'Prefer': 'return=representation',
      },
      body: JSON.stringify({ reason }),
    })

    if (!response.ok) {
      const error = await response.json()
      throw new Error(error.message || 'Failed to update absence reason')
    }

    const data: any[] = await response.json()
    if (!data[0]) throw new NotFoundError('Absence not found')

    // TODO: writeAuditEntry('absence_edited', id, ...)
    return data[0] as AbsenceRecord
  })
}

/**
 * Cancel an absence
 * - Posted assignments: PATCH status='cancelled', TODO notification
 * - Draft assignments: DELETE
 * - PATCH absence status='cancelled'
 */
export async function cancelAbsence(id: string): Promise<void> {
  return handleApiCall(async () => {
    const accessToken = getAccessToken()
    if (!accessToken) throw new Error('Not authenticated')

    // Mark posted assignments as cancelled
    await fetch(`${SUPABASE_URL}/rest/v1/assignments?absence_id=eq.${id}&status=eq.posted`, {
      method: 'PATCH',
      headers: {
        'apikey': SUPABASE_ANON_KEY,
        'Authorization': `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ status: 'cancelled' }),
    })
    // TODO: trigger cancellation notifications for posted assignments

    // Delete draft assignments
    await fetch(`${SUPABASE_URL}/rest/v1/assignments?absence_id=eq.${id}&status=eq.draft`, {
      method: 'DELETE',
      headers: {
        'apikey': SUPABASE_ANON_KEY,
        'Authorization': `Bearer ${accessToken}`,
      },
    })

    // Mark absence as cancelled
    await fetch(`${SUPABASE_URL}/rest/v1/absences?id=eq.${id}`, {
      method: 'PATCH',
      headers: {
        'apikey': SUPABASE_ANON_KEY,
        'Authorization': `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ status: 'cancelled' }),
    })

    // TODO: writeAuditEntry('absence_cancelled', id, ...)
  })
}

/**
 * Reactivate a cancelled absence
 * - PATCH all cancelled assignments to status='draft'
 * - Recalculate absence status
 * - PATCH absence status
 */
export async function reactivateAbsence(id: string): Promise<AbsenceRecord> {
  return handleApiCall(async () => {
    const accessToken = getAccessToken()
    if (!accessToken) throw new Error('Not authenticated')

    // Fetch absence
    const absence = await getAbsenceById(id)
    if (absence.status !== 'cancelled') {
      throw new ValidationError('Only cancelled absences can be reactivated', {
        status: 'Absence is not cancelled',
      })
    }

    // Revert cancelled assignments to draft
    await fetch(`${SUPABASE_URL}/rest/v1/assignments?absence_id=eq.${id}&status=eq.cancelled`, {
      method: 'PATCH',
      headers: {
        'apikey': SUPABASE_ANON_KEY,
        'Authorization': `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ status: 'draft' }),
    })

    // Fetch updated assignments to recalculate status
    const assignmentsParams = new URLSearchParams()
    assignmentsParams.append('absence_id', `eq.${id}`)
    assignmentsParams.append('select', 'status')

    const assignmentsResponse = await fetch(
      `${SUPABASE_URL}/rest/v1/assignments?${assignmentsParams}`,
      {
        headers: {
          'apikey': SUPABASE_ANON_KEY,
          'Authorization': `Bearer ${accessToken}`,
        },
      }
    )

    let newStatus: AbsenceStatus = 'pending'
    if (assignmentsResponse.ok) {
      const assignments = await assignmentsResponse.json()
      newStatus = deriveAbsenceStatusFromAssignments(assignments)
    }

    // Update absence status
    const response = await fetch(`${SUPABASE_URL}/rest/v1/absences?id=eq.${id}`, {
      method: 'PATCH',
      headers: {
        'apikey': SUPABASE_ANON_KEY,
        'Authorization': `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
        'Prefer': 'return=representation',
      },
      body: JSON.stringify({ status: newStatus }),
    })

    if (!response.ok) {
      const error = await response.json()
      throw new Error(error.message || 'Failed to reactivate absence')
    }

    const data: any[] = await response.json()
    // TODO: writeAuditEntry('absence_reactivated', id, ...)
    return data[0] as AbsenceRecord
  })
}

/**
 * Cancel a specific day of an absence
 * - Posted assignments: PATCH status='cancelled', TODO notification
 * - Draft only: DELETE
 */
export async function cancelAbsenceDay(absenceId: string, date: string): Promise<void> {
  return handleApiCall(async () => {
    const accessToken = getAccessToken()
    if (!accessToken) throw new Error('Not authenticated')

    // Fetch assignments for this date
    const assignmentsParams = new URLSearchParams()
    assignmentsParams.append('absence_id', `eq.${absenceId}`)
    assignmentsParams.append('date', `eq.${date}`)
    assignmentsParams.append('select', 'id,status')

    const assignmentsResponse = await fetch(
      `${SUPABASE_URL}/rest/v1/assignments?${assignmentsParams}`,
      {
        headers: {
          'apikey': SUPABASE_ANON_KEY,
          'Authorization': `Bearer ${accessToken}`,
        },
      }
    )

    if (assignmentsResponse.ok) {
      const assignments = await assignmentsResponse.json()
      const hasPosted = assignments.some((a: any) => a.status === 'posted')

      if (hasPosted) {
        // Mark posted assignments as cancelled
        await fetch(
          `${SUPABASE_URL}/rest/v1/assignments?absence_id=eq.${absenceId}&date=eq.${date}&status=eq.posted`,
          {
            method: 'PATCH',
            headers: {
              'apikey': SUPABASE_ANON_KEY,
              'Authorization': `Bearer ${accessToken}`,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ status: 'cancelled' }),
          }
        )
        // TODO: trigger cancellation notifications
      }

      // Delete draft assignments
      await fetch(
        `${SUPABASE_URL}/rest/v1/assignments?absence_id=eq.${absenceId}&date=eq.${date}&status=eq.draft`,
        {
          method: 'DELETE',
          headers: {
            'apikey': SUPABASE_ANON_KEY,
            'Authorization': `Bearer ${accessToken}`,
          },
        }
      )
    }

    // TODO: writeAuditEntry('day_cancelled', absenceId, ..., target_date=date)
  })
}

/**
 * Reactivate a cancelled day
 * - Fetch parent absence — if status='cancelled', throw ValidationError
 * - PATCH cancelled assignments for this date to status='draft'
 */
export async function reactivateAbsenceDay(absenceId: string, date: string): Promise<void> {
  return handleApiCall(async () => {
    const accessToken = getAccessToken()
    if (!accessToken) throw new Error('Not authenticated')

    // Fetch parent absence
    const absence = await getAbsenceById(absenceId)
    if (absence.status === 'cancelled') {
      throw new ValidationError('Cannot reactivate a day when the entire absence is cancelled', {
        absence_status: 'Parent absence is cancelled',
      })
    }

    // Revert cancelled assignments to draft
    await fetch(
      `${SUPABASE_URL}/rest/v1/assignments?absence_id=eq.${absenceId}&date=eq.${date}&status=eq.cancelled`,
      {
        method: 'PATCH',
        headers: {
          'apikey': SUPABASE_ANON_KEY,
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ status: 'draft' }),
      }
    )

    // TODO: writeAuditEntry('day_reactivated', absenceId, ..., target_date=date)
  })
}
